import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { Toast, ToastData, ToastType } from './Toast';

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => void;
  showSuccess: (message: string, options?: ToastOptions) => void;
  showError: (message: string, options?: ToastOptions) => void;
  showInfo: (message: string, options?: ToastOptions) => void;
  showWarning: (message: string, options?: ToastOptions) => void;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

interface ToastOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }>;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children, 
  maxToasts = 3 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const showToast = useCallback((
    message: string, 
    type: ToastType = 'info',
    options?: ToastOptions
  ) => {
    const newToast: ToastData = {
      id: generateId(),
      message,
      type,
      duration: options?.duration,
      action: options?.action,
      actions: options?.actions
    };

    setToasts(prev => {
      const updated = [newToast, ...prev];
      // Limit number of visible toasts
      return updated.slice(0, maxToasts);
    });
  }, [maxToasts]);

  const showSuccess = useCallback((message: string, options?: ToastOptions) => {
    showToast(message, 'success', options);
  }, [showToast]);

  const showError = useCallback((message: string, options?: ToastOptions) => {
    showToast(message, 'error', { duration: 6000, ...options }); // Errors stay longer
  }, [showToast]);

  const showInfo = useCallback((message: string, options?: ToastOptions) => {
    showToast(message, 'info', options);
  }, [showToast]);

  const showWarning = useCallback((message: string, options?: ToastOptions) => {
    showToast(message, 'warning', options);
  }, [showToast]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = useMemo(
    () => ({
      showToast,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      dismissToast,
      dismissAll
    }),
    [
      showToast,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      dismissToast,
      dismissAll
    ]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Toast container */}
      <Box
        sx={{
          position: 'fixed',
          bottom: isMobile ? 80 : 24, // Above mobile nav if present
          left: isMobile ? '50%' : 24,
          transform: isMobile ? 'translateX(-50%)' : 'none',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 1,
          pointerEvents: 'none',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : 400
        }}
      >
        {toasts.map((toast) => (
          <Toast 
            key={toast.id} 
            toast={toast} 
            onDismiss={dismissToast} 
          />
        ))}
      </Box>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
